import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { getAuditLogs, getAuditSummary } from '../lib/api';
import { Eye, FunnelSimple, ArrowClockwise, User, Warning } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const methodColors = {
  GET: 'bg-muted text-foreground',
  POST: 'bg-blue-100 text-blue-800',
  PUT: 'bg-amber-100 text-amber-800',
  PATCH: 'bg-amber-100 text-amber-800',
  DELETE: 'bg-red-100 text-red-800',
};

export default function AuditLog() {
  const [summary, setSummary] = useState(null);
  const [logs, setLogs] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    user_email: '',
    method: '',
    path_contains: '',
    min_status: '',
  });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.user_email) params.user_email = filters.user_email;
      if (filters.method) params.method = filters.method;
      if (filters.path_contains) params.path_contains = filters.path_contains;
      if (filters.min_status) params.min_status = parseInt(filters.min_status);
      params.limit = 200;

      const [sumRes, logsRes] = await Promise.all([
        getAuditSummary(7),
        getAuditLogs(params),
      ]);
      setSummary(sumRes.data);
      setLogs(logsRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  return (
    <Layout>
      <div data-testid="audit-log-page" className="p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <Eye size={32} weight="fill" className="text-foreground" />
              Audit Log
            </h1>
            <p className="text-muted-foreground">Immutable record of every mutation across the WMS</p>
          </div>
          <Button
            onClick={fetchAll}
            data-testid="audit-refresh-btn"
            variant="outline"
          >
            <ArrowClockwise size={16} className="mr-2" /> Refresh
          </Button>
        </div>

        {/* Summary cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-card border border-border rounded-lg p-4">
              <p className="overline">Calls (7d)</p>
              <p className="text-3xl font-bold">{summary.total_calls.toLocaleString()}</p>
            </div>
            <div className={`border p-4 ${summary.error_count > 0 ? 'bg-red-50 border-red-200' : 'bg-card border-border rounded-lg'}`}>
              <p className="overline">Errors (4xx/5xx)</p>
              <p className={`text-3xl font-bold ${summary.error_count > 0 ? 'text-red-700' : ''}`}>
                {summary.error_count.toLocaleString()}
              </p>
              <p className="text-xs text-muted-foreground">{summary.error_rate_pct}% error rate</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <p className="overline">Top User</p>
              <p className="text-sm font-bold truncate">{summary.top_users[0]?.email || '—'}</p>
              <p className="text-xs text-muted-foreground">{summary.top_users[0]?.count || 0} calls</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <p className="overline">Methods</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {summary.per_method.map(m => (
                  <span key={m.method} className={`${methodColors[m.method] || 'bg-muted'} px-1.5 py-0.5 text-xs font-bold`}>
                    {m.method} {m.count}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-card border border-border rounded-lg p-4 mb-4 grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
          <div>
            <Label className="overline">User Email</Label>
            <Input
              placeholder="admin@stockaris.com"
              value={filters.user_email}
              onChange={(e) => setFilters({ ...filters, user_email: e.target.value })}
              className="mt-1 font-mono text-sm"
              data-testid="audit-filter-user"
            />
          </div>
          <div>
            <Label className="overline">Method</Label>
            <select
              value={filters.method}
              onChange={(e) => setFilters({ ...filters, method: e.target.value })}
              className="mt-1 w-full px-3 py-2 border border-border font-medium text-sm"
              data-testid="audit-filter-method"
            >
              <option value="">All</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="PATCH">PATCH</option>
              <option value="DELETE">DELETE</option>
            </select>
          </div>
          <div>
            <Label className="overline">Path contains</Label>
            <Input
              placeholder="/api/orders"
              value={filters.path_contains}
              onChange={(e) => setFilters({ ...filters, path_contains: e.target.value })}
              className="mt-1 font-mono text-sm"
              data-testid="audit-filter-path"
            />
          </div>
          <div>
            <Label className="overline">Min Status</Label>
            <Input
              type="number"
              placeholder="400"
              value={filters.min_status}
              onChange={(e) => setFilters({ ...filters, min_status: e.target.value })}
              className="mt-1 font-mono text-sm"
              data-testid="audit-filter-status"
            />
          </div>
          <Button onClick={fetchAll} className="bg-brand hover:bg-brand/90" data-testid="audit-apply-filters">
            <FunnelSimple size={16} className="mr-2" /> Apply
          </Button>
        </div>

        <div className="bg-card border border-border rounded-lg overflow-x-auto">
          <p className="px-4 py-2 text-xs text-muted-foreground border-b border-border">
            Showing {logs.items.length} of {logs.total.toLocaleString()} total entries
          </p>
          <table className="w-full table-dense">
            <thead>
              <tr>
                <th>Time</th>
                <th>Method</th>
                <th>Path</th>
                <th>Status</th>
                <th>User</th>
                <th>Role</th>
                <th>Duration</th>
                <th>IP</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">Loading...</td></tr>
              ) : logs.items.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-12">
                  <Eye size={40} className="mx-auto mb-2 text-slate-300" />
                  <p className="text-muted-foreground">No audit log entries match filters</p>
                </td></tr>
              ) : logs.items.map(log => (
                <tr key={log.id} data-testid={`audit-row-${log.id}`}>
                  <td className="text-xs text-muted-foreground font-mono">
                    {log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}
                  </td>
                  <td>
                    <span className={`${methodColors[log.method] || 'bg-muted'} px-2 py-0.5 text-xs font-bold`}>
                      {log.method}
                    </span>
                  </td>
                  <td className="font-mono text-sm max-w-md truncate">{log.path}</td>
                  <td>
                    <span className={`font-mono font-bold text-sm ${log.status_code >= 500 ? 'text-red-700' : log.status_code >= 400 ? 'text-amber-700' : 'text-green-700'}`}>
                      {log.status_code}
                      {log.status_code >= 400 && <Warning size={12} weight="bold" className="inline ml-1" />}
                    </span>
                  </td>
                  <td className="text-sm">
                    <div className="flex items-center gap-1">
                      <User size={12} />
                      {log.user_email || <span className="text-muted-foreground italic">anon</span>}
                    </div>
                  </td>
                  <td className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{log.user_role || '-'}</td>
                  <td className="font-mono text-sm">{log.duration_ms}ms</td>
                  <td className="font-mono text-xs text-muted-foreground">{log.client_host || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
